import './App.scss';
import React, {
  Dispatch,
  SetStateAction,
  Suspense,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { BrowserRouter as Router, Route, Routes, useNavigate } from 'react-router';
import { initializeLogging } from './utils/logger-utils';
import { AppSettings, FeatureFlag } from './utils/appSettings';
import {
  SubscriptionErrorPageRoute,
  SubscriptionSessionTestPageRoute,
  InstalmentPlanErrorPageRoute,
  InstalmentPlanStatusRoute,
  InstalmentPlanSessionTestPageRoute,
  InstalmentPlanPayByCardRoute,
  InstalmentPlanSuccessPageRoute,
  InstalmentPlanUpdatePaymentMethodRoute,
  SubscriptionFailurePageRoute,
  SubscriptionPayByCardRoute,
  SubscriptionSuccessPageRoute,
  SubscriptionUpdatePaymentRoute,
} from './RouteComponents';
import { nppRoutes } from './routes';
import PaymentWrapper from './components/common/wrappers/payment/paymentWrappers';
import { commonPageOptions } from './routes/commonOptions';
import {
  PaymentExpiredPage,
  SessionTestPage,
  PaymentMethodChangeLoadingWrapper,
  PaymentErrorPage,
} from './LazyLoadComponents';
import GenericErrorPage from './components/core/GenericErrorPage';
import {
  PaymentMethodContextProvider,
  ErrorSummaryContextProvider,
  NotificationsProvider,
  ExperimentProvider,
} from '@contexts';
import BootLoader from './shared/BootLoader';
import {
  GenericErrorPath,
  InstalmentPlanErrorPath,
  PaymentErrorPagePath,
  SubscriptionErrorPagePath,
} from './routes/PathConsts';
import { CustomCspReload } from './utils/customCspReload';
import { setNavigate } from './utils/navigateRef';
import { startRevealPolling } from './utils/initialLoader';
interface Props {
  settings: AppSettings;
  featureManagement: FeatureFlag;
}

type LoadingContextType = {
  isLoading: boolean;
  setIsLoading: Dispatch<SetStateAction<boolean>>;
};
const LoadingContext = React.createContext<LoadingContextType>({} as LoadingContextType);
export function useLoadingContext() {
  return useContext<LoadingContextType>(LoadingContext);
}

const NavigateBridge = () => {
  const navigate = useNavigate();
  useEffect(() => {
    setNavigate(navigate);
  }, [navigate]);
  return null;
};

export const App = (props: Props) => {
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    return initializeLogging(props.settings);
  }, [props.settings]);

  // Watches for the shell to actually paint; ends the static preview overlay.
  useEffect(startRevealPolling, []);

  const getFullRoute = (routeBase: string, subRoute: string) => {
    if (subRoute.length) {
      return `${routeBase}/${subRoute}`;
    }

    return routeBase;
  };

  const enabledRoutes = useMemo(() => {
    return nppRoutes(props.featureManagement);
  }, [props.featureManagement]);

  return (
    <LoadingContext.Provider value={{ isLoading, setIsLoading }}>
      <NotificationsProvider>
        <Router>
          <NavigateBridge />
          <ErrorSummaryContextProvider>
            <ExperimentProvider>
              <PaymentMethodContextProvider>
                <CustomCspReload>
                  <Suspense fallback={<BootLoader />}>
                    <Routes>
                      <Route
                        path="/stripe/subscription/cardpayment"
                        element={<SubscriptionPayByCardRoute />}
                      />
                      <Route
                        path="/stripe/subscription/success"
                        element={<SubscriptionSuccessPageRoute />}
                      />
                      <Route
                        path="/stripe/subscription/paymentmethod"
                        element={<SubscriptionUpdatePaymentRoute />}
                      />
                      <Route
                        path="/stripe/subscription/failure"
                        element={<SubscriptionFailurePageRoute />}
                      />
                      <Route
                        path="/stripe/instalmentplan/cardpayment"
                        element={<InstalmentPlanPayByCardRoute />}
                      />
                      <Route
                        path="/stripe/instalmentplan/success"
                        element={<InstalmentPlanSuccessPageRoute />}
                      />
                      <Route
                        path="/stripe/instalmentplan/paymentmethod"
                        element={<InstalmentPlanUpdatePaymentMethodRoute />}
                      />
                      <Route
                        path={SubscriptionErrorPagePath}
                        element={<SubscriptionErrorPageRoute />}
                      />
                      <Route
                        path={InstalmentPlanErrorPath}
                        element={<InstalmentPlanErrorPageRoute />}
                      />
                      <Route
                        path="/subscription/stp"
                        element={<SubscriptionSessionTestPageRoute />}
                      />
                      <Route
                        path="/instalmentplan/stp"
                        element={<InstalmentPlanSessionTestPageRoute />}
                      />
                      <Route path="/instalmentplan/status" element={<InstalmentPlanStatusRoute />} />

                      {Object.keys(enabledRoutes).map((key) => {
                        const route = enabledRoutes[key];
                        return route.subRoutes.map((subRoute) => {
                          if (Array.isArray(subRoute.path)) {
                            return subRoute.path.map((subRoutePath) => {
                              return (
                                <Route
                                  key={getFullRoute(route.base, subRoutePath)}
                                  path={getFullRoute(route.base, subRoutePath)}
                                  element={<PaymentWrapper options={subRoute.options} />}
                                >
                                  <Route
                                    index
                                    element={
                                      <Suspense fallback={<BootLoader />}>
                                        <PaymentMethodChangeLoadingWrapper>
                                          {subRoute.element}
                                        </PaymentMethodChangeLoadingWrapper>
                                      </Suspense>
                                    }
                                  />
                                </Route>
                              );
                            });
                          }

                          return (
                            <Route
                              key={getFullRoute(route.base, subRoute.path)}
                              path={getFullRoute(route.base, subRoute.path)}
                              element={<PaymentWrapper options={subRoute.options} />}
                            >
                              <Route
                                index
                                element={
                                  <Suspense fallback={<BootLoader />}>
                                    <PaymentMethodChangeLoadingWrapper>
                                      {subRoute.element}
                                    </PaymentMethodChangeLoadingWrapper>
                                  </Suspense>
                                }
                              />
                            </Route>
                          );
                        });
                      })}
                      <Route path="/" element={<PaymentWrapper />}>
                        <Route path="expired" element={<PaymentExpiredPage />} />
                        <Route path="stp" element={<SessionTestPage />} />
                      </Route>
                      <Route path="/" element={<PaymentWrapper options={commonPageOptions.error} />}>
                        <Route path={PaymentErrorPagePath} element={<PaymentErrorPage />} />
                      </Route>
                      <Route path={GenericErrorPath} element={<GenericErrorPage />} />
                      <Route path="/*" element={<GenericErrorPage />} />
                    </Routes>
                  </Suspense>
                </CustomCspReload>
              </PaymentMethodContextProvider>
            </ExperimentProvider>
          </ErrorSummaryContextProvider>
        </Router>
      </NotificationsProvider>
    </LoadingContext.Provider>
  );
};

export interface State {
  errorResponse?: string;
}
